import React, { FunctionComponent, useState } from 'react';
import InputMask from 'react-input-mask';
import LeadModal from 'components/Leads/LeadModal/LeadModal';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import Button from 'components/UI/V3/Button/Button';
import Client, { LeadStage, LeadSource } from '@spike/client-model';
import { cleanPhoneNumberFormat } from '@spike/phone-utils';
import { v4 as uuidv4 } from 'uuid';
import styles from './LeadForm.module.scss';
import AlertMessage from 'components/UI/V3/AlertMessage';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

interface LeadFormProps {
    mode: 'add' | 'edit';
    lead?: Client;
    onClose: () => void;
    onSave?: (lead: Client) => void;
    isSaving?: boolean;
}

interface FormData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
}

export const sourceOptions = [
    { value: LeadSource.Manual, label: 'Manually' },
    { value: LeadSource.IntakeForm, label: 'Intake Form' },
    { value: LeadSource.AbandonedCart, label: 'Abandoned Cart' },
    { value: LeadSource.UnknownSms, label: 'Unknown Number' }
];

const LeadForm: FunctionComponent<LeadFormProps> = ({ mode, lead, onClose, onSave, isSaving }) => {
    const [formData, setFormData] = useState<FormData>({
        firstName: lead?.firstName || '',
        lastName: lead?.lastName || '',
        phone: lead?.phone || '',
        email: lead?.email || ''
    });

    const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

    const handleInputChange = (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof FormData, string>> = {};

        if (!formData.email.trim() && !formData.phone.trim()) {
            newErrors.email = 'Either email or phone number is required';
            newErrors.phone = 'Either email or phone number is required';
        }

        if (formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Please enter a valid email address';
            }
        }

        if (formData.phone.trim()) {
            if (formData.phone.replace(/\D/g, '').length < 10) {
                newErrors.phone = 'Please enter a valid phone number';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validateForm()) return;

        const savedClient: Client = {
            ...(lead || {}),
            id: lead?.id,
            uuid: lead?.uuid || uuidv4(),
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email.trim() || '',
            phone: formData.phone.trim() || '',
            pets: lead?.pets || [],
            active: true,
            image: lead?.image || '',
            notes: lead?.notes || '',
            authorizedContacts: lead?.authorizedContacts || [],
            temporalEmail: !formData.email.trim() && !lead?.email,
            tags: lead?.tags || { positive: [], negative: [] },
            destroyable: lead?.destroyable ?? false,
            notifications: lead?.notifications || { email: false, phone: false, marketing: false },
            blocked: lead?.blocked ?? false,
            customQuestionResponses: lead?.customQuestionResponses || [],
            hasActiveMembership: lead?.hasActiveMembership ?? false,
            clientSince: lead?.clientSince ?? null,
            leadStage: lead?.leadStage ?? LeadStage.New,
            leadSource: mode === 'add' ? LeadSource.Manual : lead?.leadSource ?? LeadSource.Manual,
            conversionSource: lead?.conversionSource ?? null
        };

        onSave?.(savedClient);
        // onClose() intentionally omitted — provider closes the form on SaveSuccess
    };

    const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const digits = cleanPhoneNumberFormat(event.target.value);
        setFormData(prev => ({ ...prev, phone: digits }));
        if (errors.phone) {
            setErrors(prev => ({ ...prev, phone: undefined }));
        }
    };

    const footer = (
        <div className={styles.footer}>
            <Button
                label="Discard"
                variant="black"
                size="large"
                onClick={onClose}
                className={styles.discardButton}
            />
            <Button
                label="Save"
                variant="green"
                size="large"
                onClick={handleSave}
                isLoading={isSaving}
                disabled={isSaving}
                className={styles.saveButton}
            />
        </div>
    );

    return (
        <LeadModal
            title={mode === 'edit' ? 'Edit Lead' : 'Add New Lead'}
            footer={footer}
            onClose={onClose}
            className={styles.modal}
        >
            <div className={styles.content}>
                <div className={styles.alertSection}>
                    <AlertMessage
                        icon={faInfoCircle}
                        bgColor="#F8F5F1"
                        bdColor="#BAA997"
                    >
                        Create a lead using an email or phone number.
                    </AlertMessage>
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>First Name</label>
                    <Input
                        value={formData.firstName}
                        onChange={handleInputChange('firstName')}
                        placeholder="First Name"
                        className={styles.input}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>Last Name</label>
                    <Input
                        value={formData.lastName}
                        onChange={handleInputChange('lastName')}
                        placeholder="Last Name"
                        className={styles.input}
                    />
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        Phone Number {errors.phone && <span className={styles.required}>*</span>}
                    </label>
                    <InputMask
                        mask="(999) 999-9999"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                    >
                        {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                            <input
                                {...inputProps}
                                placeholder="(___) ___-____"
                                className={clsx(styles.phoneInput, {
                                    [styles.phoneInputError]: !!errors.phone
                                })}
                            />
                        )}
                    </InputMask>
                    {errors.phone && <span className={styles.errorText}>{errors.phone}</span>}
                </div>

                <div className={styles.formGroup}>
                    <label className={styles.label}>
                        Email {errors.email && <span className={styles.required}>*</span>}
                    </label>
                    <Input
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        placeholder="Email"
                        type="email"
                        className={styles.input}
                        error={errors.email}
                    />
                    {errors.email && <span className={styles.errorText}>{errors.email}</span>}
                </div>
            </div>
        </LeadModal>
    );
};

export default LeadForm;
